import './map-marker-info-window.css';

import { IStoreLocation, IStoreLocationHour } from '../../../models/data.interface';
import { Util } from '../../../utils/util';
import { Data } from '../../../models/data';
import { LocalizationService } from '../../../services/localization-service';

export class MapMarkerInfoWindow {
    private readonly _data: Data;
    private readonly _localizationService: LocalizationService;

    constructor(data: Data, localizationService: LocalizationService) {
        this._data = data;
        this._localizationService = localizationService;
    }

    /**
     * Creates an HTML string for an InfoWindow displaying store details, such as name, address, phone, and hours.
     * @param {IStoreLocation} store - The store location object containing details about the store.
     * @returns {HTMLElement} HTML element representing the element of the InfoWindow.
     */
    public createStoreInfoWindow(store: IStoreLocation, languageCode: string, onStartOrderClick?: () => void, buttonLabelKey?: string): HTMLElement {
        const addressObject = this._formatAddress(store);
        const phone = this._formatPhone(store.phone, store.countryId || 1);
        const hoursHtml = this._generateHoursHtml(store.hours, languageCode);

        const contentString = `
            <div class="store-info-window">
                <h2>${store.name}</h2>
                <p>
                    <strong>${this._localizationService.names("Address")}:</strong><br>
                    ${addressObject.address1},<br>
                    ${addressObject.address3 ? `${addressObject.address3}<br>,` : ""}
                    ${addressObject.cityState}
                </p>
                <p>
                    <strong>${this._localizationService.names("Phone")}:</strong><br>
                    ${phone}
                </p>

                <div>
                    <h3>${this._localizationService.names("StoreHours")}</h3>
                    <ul>
                        ${hoursHtml}
                    </ul>
                </div>
            </div>`;

        const htmlElement = Util.createHtmlElementFromTemplate(contentString);

        if (onStartOrderClick) {
            const button = document.createElement("button");
            button.textContent = buttonLabelKey ? this._localizationService.names(buttonLabelKey) : this._localizationService.names("StartOrder");
            button.addEventListener("click", onStartOrderClick);

            const divElement = document.createElement("div");
            divElement.classList.add("start-order-button-container");
            divElement.appendChild(button);

            htmlElement.appendChild(divElement);
        }

        return htmlElement;
    }

    /**
     * Formats the store's address for display.
     * @param {IStoreLocation} store - The store location data.
     * @returns {IAddressFormat} The formatted address.
     */
    private readonly _formatAddress = (store: IStoreLocation): { address1: string, address2: string, address3: string, cityState: string, country: string } => {
        return Util.formatAddressObject({
            StreetNo: store.streetNumber,
            Address1: store.address1,
            Address2: store.address2,
            Address3: store.address3,
            Address4: store.address4,
            Address5: store.address5,
            City: store.city,
            State: store.state,
            ZipCode: store.zip,
            CountryID: store.countryId || 1,
            AddressTypeID: 0
        }, this._data.getBrandProperty("Countries"));
    };

    /**
     * Formats a phone number based on the country ID.
     * @param {string} phone - The phone number to format.
     * @param {number} countryId - The ID of the country for formatting.
     * @returns {string} The formatted phone number.
     */
    private _formatPhone(phone: string, countryId: number): string {
        const phoneFormatLanguage = Util.getDefaultCultureCode(countryId, this._data.getBrandProperty("Countries"));
        return Util.formatPhoneNumber(phone, this._localizationService.names("PhoneFormat", phoneFormatLanguage));
    };

    /**
     * Generates an HTML string for the store's hours of operation for the current week.
     * @param {IStoreLocationHour[]} hours - Array of store hours for each weekday.
     * @returns {string} HTML string representing the hours of operation.
     */
    private _generateHoursHtml(hours: IStoreLocationHour[], languageCode: string): string {
        const currentWeekday = new Date().getDay();
        let hoursHtml = "";

        for (let i = currentWeekday; i < currentWeekday + 7; i++) {
            const weekdayId = i % 7;
            const dayHours = hours.find(hour => hour.weekdayId === weekdayId);
            const hoursText = dayHours
                ? `${Util.formatTimeByIsoCode(dayHours.startMinute, languageCode)} - ${Util.formatTimeByIsoCode(dayHours.endMinute, languageCode)}`
                : this._localizationService.names("Closed");

            const weekdayName = Util.getWeekdayNameById(weekdayId);
            hoursHtml += `
            <li>
                <span>${this._localizationService.names(weekdayName)}:</span> <span>${hoursText}</span>
            </li>`;
        }

        return hoursHtml;
    };
}